import { DATE_PRESETS, activePreset, presetRange, type Filters, type FiltersResponse } from '../lib/reports';

interface DatePresetsProps<K extends string> {
  from: string;
  to: string;
  onChange: (patch: { from: string; to: string }) => void;
  /** Preset chips to render. Defaults to the shared rolling presets. */
  presets?: { key: K; label: string }[];
  /** Resolve a preset key to a concrete range. */
  resolve?: (key: K) => { from: string; to: string };
  /** Which preset (if any) the current range matches. */
  active?: (from: string, to: string) => K | null;
}

/**
 * Quick date-range chips. Defaults to the shared rolling presets
 * (Yesterday / Last week / Last month) but any form can pass its own set —
 * the cost report supplies Mon–Sun costing weeks, for example.
 */
export function DatePresets<K extends string = string>({
  from,
  to,
  onChange,
  presets,
  resolve,
  active,
}: DatePresetsProps<K>) {
  const list = (presets ?? (DATE_PRESETS as unknown as { key: K; label: string }[]));
  const resolveRange = resolve ?? (presetRange as unknown as (key: K) => { from: string; to: string });
  const activeKey = (active ?? (activePreset as unknown as (f: string, t: string) => K | null))(from, to);
  return (
    <div className="date-presets" role="group" aria-label="Quick date ranges">
      {list.map((p) => (
        <button
          key={p.key}
          type="button"
          className={`preset${activeKey === p.key ? ' active' : ''}`}
          aria-pressed={activeKey === p.key}
          onClick={() => onChange(resolveRange(p.key))}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

interface FilterBarProps {
  filters: Filters;
  options: FiltersResponse | null;
  onChange: (patch: Partial<Filters>) => void;
  onExport?: () => void;
  exportLabel?: string;
  showDriver?: boolean;
}

/** Shared date-range + region/customer/driver controls for every report. */
export function FilterBar({
  filters,
  options,
  onChange,
  onExport,
  exportLabel = 'Export',
  showDriver = true,
}: FilterBarProps) {
  return (
    <div className="filters">
      <div className="filter">
        <span className="filter-label">Date range</span>
        <div className="filter-range">
          <input type="date" value={filters.from} max={filters.to} onChange={(e) => onChange({ from: e.target.value })} />
          <span className="filter-arrow">→</span>
          <input type="date" value={filters.to} min={filters.from} onChange={(e) => onChange({ to: e.target.value })} />
        </div>
        <DatePresets from={filters.from} to={filters.to} onChange={onChange} />
      </div>

      <div className="filter">
        <span className="filter-label">Team region</span>
        <select value={filters.region} onChange={(e) => onChange({ region: e.target.value })}>
          <option value="">All regions</option>
          {options?.regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      <div className="filter">
        <span className="filter-label">Store</span>
        <select value={filters.customer} onChange={(e) => onChange({ customer: e.target.value })}>
          <option value="">All stores</option>
          {options?.customers.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {showDriver && (
        <div className="filter">
          <span className="filter-label">Driver</span>
          <select value={filters.driver} onChange={(e) => onChange({ driver: e.target.value })}>
            <option value="">All drivers</option>
            {options?.drivers.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      )}

      {onExport && (
        <button type="button" className="btn-export" onClick={onExport}>
          ↓ {exportLabel}
        </button>
      )}
    </div>
  );
}
